import {
  BadgeCheckIcon,
  BanknoteIcon,
  BellIcon,
  ClockIcon,
  InboxIcon,
  PackageIcon,
  ReceiptIcon,
  TruckIcon,
  XIcon,
} from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Skeleton } from "@/components/ui/skeleton"
import {
  describeNotification,
  type NotificationIconName,
} from "@/features/notifications/notification-copy"
import {
  isModuleOff,
  useMarkNotificationRead,
  useNotifications,
} from "@/features/notifications/use-notifications"
import { ApiError } from "@/lib/api-error"
import { istDayOfInstant, useFormatDay } from "@/lib/dates"
import type { NotificationRow } from "@/lib/endpoints"

/**
 * The inbox list itself — ONE component, rendered by two surfaces (§17.7).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE DRAWER AND THE `/notifications` ROUTE SHOW THE SAME ROWS BECAUSE THEY ARE
 * THE SAME COMPONENT.
 *
 * The bell opens a drawer; the route still exists because links that were sent
 * over WhatsApp months ago point at it, and a notification email will point at
 * it again (§17A). Two implementations of "the inbox" is how the drawer ends up
 * marking read on open while the page does not — a difference nobody would see
 * until somebody swore they had never been told something.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * THE CROSS MARKS READ AND HIDES. IT DOES NOT DELETE, AND THERE IS NO ENDPOINT
 * THAT COULD.
 *
 * `routes/notifications.ts` has no DELETE and says why: the rows stay, with
 * their `read_at`, so "was anybody ever told this bill needed an e-way bill" has
 * an answer months later. That answer is what a detention dispute turns on — a
 * transporter's claim against a record that somebody was told, on a date. An
 * inbox that empties itself when cleared cannot produce it.
 *
 * So the cross is `POST /:id/read` plus a hide THAT LIVES IN THIS COMPONENT'S
 * STATE and nowhere else: it survives no refresh, changes nothing on the server
 * beyond the read timestamp, and the row is one tap away again on the full list.
 * Same for "Clear all", which is `POST /read-all` — the endpoint that already
 * exists — plus the same local hide.
 */

/**
 * Rows hidden in THIS view, for THIS visit.
 *
 * Deliberately not persisted and deliberately not in the query cache: a hide is
 * a gesture about the panel in front of you ("I have dealt with these"), not a
 * fact about the notification. Persisting it would be a delete with extra steps
 * — the thing the API refuses to have.
 */
export interface DismissedRows {
  /**
   * Takes the ROW, not an id — "cleared" is answered by `readAt`, and an id
   * alone cannot answer it. See `clearAll`.
   */
  isHidden: (row: Pick<NotificationRow, "id" | "readAt">) => boolean
  dismiss: (id: number) => void
  /**
   * "Clear all" — call ONLY after `POST /read-all` has actually succeeded.
   * `ids` are the rows on screen at the time; see below for why they are not
   * the whole story.
   */
  clearAll: (ids: readonly number[]) => void
}

/**
 * TWO HIDING RULES, AND THE SECOND ONE EXISTS BECAUSE IDS ALONE LEAK ROWS.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * The obvious implementation — "Clear all hides the ids currently loaded" —
 * breaks the moment the inbox passes the page size, and this inbox is designed
 * never to shrink (nothing is ever deleted), so it gets there.
 *
 * The server orders `[{ readAt: "asc" }, { createdAt: "desc" }, { id: "desc" }]`
 * and takes 50 (`routes/notifications.ts`). Unread rows sort first, because
 * NULL is first under MariaDB's `ASC`. `POST /read-all` then stamps every unread
 * row with ONE fresh `read_at`, later than every timestamp already stored — so
 * those rows fall to the BOTTOM of the ordering and rows that were on page 2
 * rise onto page 1. With 60 notifications, 10 of them unread, "Clear all"
 * captures 50 ids, the refetch returns 10 ids that were never among them, and
 * the drawer the owner just cleared still shows ten rows.
 *
 * So the ids handle the immediate frame — the rows vanish on the tap rather than
 * a round trip later — and a STICKY "cleared" flag handles everyone who arrives
 * afterwards: once `read-all` has succeeded, every row the server reports as
 * read was covered by it, by definition, because the endpoint marks the caller's
 * whole inbox and not a page of it. Anything still unread after that arrived
 * since, and must show.
 *
 * THE FLAG IS STICKY FOR THE SURFACE'S LIFETIME, which has one consequence worth
 * naming: after a clear, opening a new alert (which marks it read) also drops it
 * out of THIS panel. That is the same sentence as the cross, and the full list at
 * `/notifications` — mounted fresh, with its own state — still has every row.
 * ─────────────────────────────────────────────────────────────────────────────
 */
interface HiddenState {
  readonly ids: ReadonlySet<number>
  readonly cleared: boolean
}

export function useDismissedNotifications(): DismissedRows {
  const [hidden, setHidden] = React.useState<HiddenState>(() => ({
    ids: new Set<number>(),
    cleared: false,
  }))

  return React.useMemo(
    () => ({
      isHidden: (row: Pick<NotificationRow, "id" | "readAt">) =>
        hidden.ids.has(row.id) || (hidden.cleared && row.readAt !== null),
      dismiss: (id: number) => {
        setHidden((current) => ({ ...current, ids: new Set(current.ids).add(id) }))
      },
      clearAll: (ids: readonly number[]) => {
        setHidden((current) => {
          const next = new Set(current.ids)
          for (const id of ids) next.add(id)
          return { ids: next, cleared: true }
        })
      },
    }),
    [hidden],
  )
}

/**
 * Icon names resolved to components.
 *
 * A total `Record` over the union in `notification-copy.ts`, so a new icon name
 * fails to compile here rather than rendering nothing. Kept in a `.tsx` file so
 * the copy module stays JSX-free and testable as plain TypeScript.
 */
const ICONS: Readonly<Record<NotificationIconName, React.ComponentType<{ className?: string }>>> = {
  ewayRequired: TruckIcon,
  ewayRecorded: BadgeCheckIcon,
  delivery: PackageIcon,
  expiring: ClockIcon,
  payment: BanknoteIcon,
  creditNote: ReceiptIcon,
  unknown: BellIcon,
}

export interface NotificationListProps {
  /** "Only what I still have to deal with". The hub's filter; the drawer's default is All. */
  unreadOnly?: boolean
  /**
   * False stops the request entirely — the drawer passes `open`, so a closed
   * drawer costs nothing. It is NOT a capability check: `notification.view` is
   * checked by the surface that mounts this, beside the thing it hides, so a
   * person who lacks it never reaches this component at all.
   */
  enabled?: boolean
  /** Hidden rows, owned by the surface so its "Clear all" can hide them too. */
  dismissed: DismissedRows
  /** Called when a row is opened — the drawer closes itself on the way out. */
  onOpenRow?: () => void
  /** The page frames the list in a `Card`; the drawer is already a surface. */
  framed?: boolean
}

export function NotificationList({
  unreadOnly = false,
  enabled = true,
  dismissed,
  onOpenRow,
  framed = true,
}: NotificationListProps): React.JSX.Element | null {
  const { t } = useTranslation()
  const query = useNotifications({ unreadOnly, enabled })

  if (query.isPending) return enabled ? <ListSkeleton framed={framed} /> : null

  /*
   * §18.3 — a 404 is the FLAG, and the surface above already renders "not
   * switched on" for it. Rendering a red "Couldn't load" here as well would tell
   * the reader something failed when nothing did.
   */
  if (query.isError) {
    if (isModuleOff(query.error)) return null

    return (
      <Alert variant="destructive">
        <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
        <AlertDescription className="flex flex-col items-start gap-3">
          <span>
            {query.error instanceof ApiError ? query.error.message : t("err.somethingWentWrong")}
          </span>
          <Button
            variant="outline"
            onClick={() => {
              void query.refetch()
            }}
          >
            {t("btn.retry")}
          </Button>
        </AlertDescription>
      </Alert>
    )
  }

  const rows = query.data.notifications
  const visible = rows.filter((row) => !dismissed.isHidden(row))

  if (rows.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <InboxIcon />
          </EmptyMedia>
          <EmptyTitle>{unreadOnly ? t("notif.emptyUnreadTitle") : t("notif.emptyTitle")}</EmptyTitle>
          <EmptyDescription>
            {unreadOnly ? t("notif.emptyUnreadHelp") : t("notif.emptyHelp")}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  /*
   * EVERYTHING HIDDEN IS NOT THE SAME SENTENCE AS "NO NOTIFICATIONS YET", and
   * saying the wrong one here is how somebody concludes the app threw their
   * alerts away. The rows are still on the server, still readable, still
   * evidence — so the words say exactly that, and point at where they are.
   */
  if (visible.length === 0) {
    return (
      <Empty data-testid="notifications-cleared">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <InboxIcon />
          </EmptyMedia>
          <EmptyTitle>{t("notif.clearedTitle")}</EmptyTitle>
          <EmptyDescription>{t("notif.clearedHelp")}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  /*
   * THE SERVER'S ORDER, UNTOUCHED — unread first, then newest first. Sorting a
   * list of dated things by date is the obvious thing to do and would be wrong
   * here: it drops an unread compliance alert from Tuesday below a read one from
   * this morning.
   */
  const list = (
    <ul className="divide-border divide-y">
      {visible.map((row) => (
        <NotificationLine key={row.id} row={row} dismissed={dismissed} onOpenRow={onOpenRow} />
      ))}
    </ul>
  )

  if (!framed) return list

  return <Card className="overflow-hidden py-0">{list}</Card>
}

function NotificationLine({
  row,
  dismissed,
  onOpenRow,
}: {
  row: NotificationRow
  dismissed: DismissedRows
  onOpenRow?: () => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const markRead = useMarkNotificationRead()
  const copy = describeNotification(row)
  const Icon = ICONS[copy.icon]

  const isUnread = row.readAt === null
  /*
   * `createdAt` IS AN ISO INSTANT, not a calendar day. Slicing it to ten
   * characters takes the UTC day and prints it as an IST one, so anything
   * written between 00:00 and 05:30 IST would report yesterday — and the owner
   * works late. `istDayOfInstant` first, `formatDay` second, every time.
   */
  const day = formatDay(istDayOfInstant(row.createdAt))

  const body = (
    <div className="flex w-full items-start gap-3 px-4 py-3 text-left">
      <span
        aria-hidden="true"
        className={
          copy.tone === "action"
            ? "bg-destructive/10 text-destructive mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full"
            : "bg-muted text-muted-foreground mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full"
        }
      >
        <Icon className="size-4" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          {/*
            * UNREAD IS CARRIED BY WEIGHT *AND* A DOT, never by colour alone —
            * §11 is a phone in a workshop, read in daylight through a screen
            * protector, and the owner is not young. Two signals, one of which
            * survives being unable to tell two greys apart.
            */}
          <span className={isUnread ? "text-sm font-semibold" : "text-sm font-normal"}>
            {t(copy.titleKey)}
          </span>
          {isUnread ? (
            <span
              data-testid="unread-dot"
              className="bg-destructive size-1.5 shrink-0 rounded-full"
              /* The word, for anyone who cannot see the dot. */
              aria-label={t("notif.unreadMarker")}
              role="img"
            />
          ) : null}
        </div>

        {copy.bodyKey === null ? null : (
          <p className="text-muted-foreground text-sm">{t(copy.bodyKey, copy.values)}</p>
        )}

        <p className="text-muted-foreground text-xs">{day}</p>
      </div>
    </div>
  )

  /*
   * THE CROSS IS OUTSIDE THE LINK, not inside it — a button nested in an anchor
   * is invalid HTML and the browser's fix-up is to close the anchor early, which
   * silently breaks the row's own destination. It carries a WORDED label rather
   * than only a glyph, because "×" alone is announced as "times" and read as
   * "delete", which is precisely what it is not.
   */
  const cross = (
    <button
      type="button"
      data-testid="notification-dismiss"
      aria-label={t("notif.dismiss")}
      disabled={markRead.isPending}
      className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring m-2 flex size-8 shrink-0 items-center justify-center self-start rounded-md focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50"
      onClick={() => {
        /*
         * READ, THEN HIDDEN — and only read if it was not already. A second
         * `POST /:id/read` is harmless (the server keeps the FIRST timestamp),
         * but there is no reason to spend a request and an audit line saying
         * again what is already recorded.
         *
         * THE HIDE WAITS FOR THE SERVER. It used to run synchronously beside
         * `mutate()`, which quietly inverted this control's promise: on a
         * dropped request the row left the screen while staying UNREAD on the
         * server, so the bell kept counting it, the panel had nowhere to show
         * it, and the alert was out of sight for the rest of the session. The
         * label says "Mark read and hide" — so it hides when it has been marked
         * read, and says so when it has not. An already-read row has nothing to
         * wait for and hides at once.
         */
        if (!isUnread) {
          dismissed.dismiss(row.id)
          return
        }

        markRead.mutate(row.id, {
          onSuccess: () => {
            dismissed.dismiss(row.id)
          },
          onError: (error) => {
            // The row STAYS, unread and in place — the failure is visible in
            // the one spot the reader is looking at, not only in a badge count.
            toast.error(error instanceof ApiError ? error.message : t("notif.markReadFailed"))
          },
        })
      }}
    >
      <XIcon className="size-4" aria-hidden="true" />
    </button>
  )

  /*
   * A ROW WITHOUT A DESTINATION IS TEXT, NOT A DEAD LINK. `link` is null when
   * the server could not name a screen for the row's entity type — a kind added
   * after this bundle was built. Rendering an anchor to nowhere would be a
   * promise the app cannot keep; rendering the sentence keeps the alert visible,
   * which is the part that matters.
   */
  if (row.link === null) {
    return (
      <li
        className={isUnread ? "bg-muted/40 flex items-start" : "flex items-start"}
        data-testid="notification-row"
      >
        <div className="min-w-0 flex-1">{body}</div>
        {cross}
      </li>
    )
  }

  return (
    <li
      className={isUnread ? "bg-muted/40 flex items-start" : "flex items-start"}
      data-testid="notification-row"
    >
      <Link
        to={row.link}
        className="hover:bg-muted/60 focus-visible:ring-ring flex min-w-0 flex-1 focus-visible:ring-2 focus-visible:outline-none"
        onClick={() => {
          /*
            * MARK-READ ON OPEN, AND THE NAVIGATION DOES NOT WAIT FOR IT. React
            * Router has already begun the transition by the time this returns;
            * awaiting a round trip so a dot can turn grey would trade the thing
            * the reader asked for against bookkeeping. A failure leaves the row
            * unread, which is the safe direction — an alert shown twice is a
            * nuisance, one silently marked read is one nobody sees again.
            */
          if (isUnread) markRead.mutate(row.id)
          onOpenRow?.()
        }}
      >
        {body}
      </Link>
      {cross}
    </li>
  )
}

function ListSkeleton({ framed }: { framed: boolean }): React.JSX.Element {
  const rows = (
    <ul className="divide-border divide-y">
      {[0, 1, 2].map((n) => (
        <li key={n} className="flex items-start gap-3 px-4 py-3">
          <Skeleton className="size-8 shrink-0 rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-full max-w-md" />
          </div>
        </li>
      ))}
    </ul>
  )

  if (!framed) return rows

  return <Card className="overflow-hidden py-0">{rows}</Card>
}
